import type { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ArrowLeft,
  CheckCircle2,
  CircleAlert,
  Eye,
  EyeOff,
  KeyRound,
  Lock,
  ShieldCheck,
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import authService from '@/services/auth.service';
import { BRAND_HERO_GRADIENT } from '@/lib/brand';
import { getApiErrorMessage } from '@/lib/api-errors';
import { meetsPasswordPolicy, PASSWORD_POLICY_MESSAGE } from '@/lib/password-policy';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() ?? '';

  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [form, setForm] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    let isMounted = true;

    const validateToken = async () => {
      if (!token) {
        if (!isMounted) return;
        setTokenError('Link inválido ou incompleto.');
        setIsTokenValid(false);
        setIsValidating(false);
        return;
      }

      try {
        await authService.validatePasswordResetToken(token);
        if (!isMounted) return;
        setIsTokenValid(true);
      } catch (error: unknown) {
        if (!isMounted) return;
        setIsTokenValid(false);
        setTokenError((error as AxiosError<{ detail?: string }>)?.response?.data?.detail ?? 'Link inválido ou expirado.');
      } finally {
        if (isMounted) {
          setIsValidating(false);
        }
      }
    };

    void validateToken();

    return () => {
      isMounted = false;
    };
  }, [token]);

  const isValid =
    isTokenValid &&
    meetsPasswordPolicy(form.newPassword) &&
    form.newPassword === form.confirmPassword;

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      await authService.resetPassword(token, form.newPassword, form.confirmPassword);
      setSubmitted(true);

      toast({
        title: 'Senha redefinida',
        description: 'Agora você já pode entrar com a nova senha.',
      });
    } catch (error: unknown) {
      const detail = (error as AxiosError<{ detail?: string }>)?.response?.data?.detail;
      toast({
        title: 'Não foi possível redefinir a senha',
        description: getApiErrorMessage(detail, 'Tente solicitar um novo link de recuperação.'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 brand-light-surface">
        <div className="w-full max-w-md space-y-8">
          <div className="lg:hidden text-center mb-8">
            <Link to="/">
              <img src="/logo-librattum.png" alt="Librattum" className="h-10 mx-auto" />
            </Link>
          </div>

          {(() => {
            if (submitted) return (
                <div className="text-center space-y-6">
                  <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center">
                    <CheckCircle2 className="h-10 w-10 text-emerald-600" />
                  </div>

                  <div className="space-y-2">
                    <h2 className="text-3xl font-bold text-gray-900">Senha atualizada</h2>
                    <p className="text-gray-600">
                      Sua senha foi redefinida com sucesso. Faça login para continuar.
                    </p>
                  </div>

                  <Link to="/login">
                    <Button className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]">
                      Ir para o login
                    </Button>
                  </Link>
                </div>
            );
            if (isValidating) return (
                <div className="text-center space-y-6">
                  <div className="mx-auto w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <KeyRound className="h-8 w-8 text-primary" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-3xl font-bold text-gray-900">Validando link</h2>
                    <p className="text-gray-600">Aguarde enquanto verificamos o token de recuperação.</p>
                  </div>
                  <div className="mx-auto h-6 w-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                </div>
            );
            if (!isTokenValid) return (
                <div className="space-y-6 text-center">
                  <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center">
                    <CircleAlert className="h-8 w-8 text-red-600" />
                  </div>
                  <div className="space-y-2">
                    <h2 className="text-3xl font-bold text-gray-900">Link indisponível</h2>
                    <p className="text-gray-600">{tokenError}</p>
                  </div>

                  <div className="space-y-3">
                    <Link to="/forgot-password">
                      <Button className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]">
                        Solicitar novo link
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button variant="outline" className="w-full h-12">
                        <ArrowLeft className="h-4 w-4 mr-2" />
                        Voltar ao login
                      </Button>
                    </Link>
                  </div>
                </div>
            );
            return (
                <>
                  <div className="text-center space-y-2">
                    <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
                      <KeyRound className="h-8 w-8 text-amber-600" />
                    </div>
                    <h2 className="text-3xl font-bold text-gray-900">Defina sua nova senha</h2>
                    <p className="text-gray-600">
                      Escolha uma senha forte para voltar a acessar sua conta.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="newPassword" className="text-gray-700">Nova Senha</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="newPassword"
                          type={showNew ? 'text' : 'password'}
                          placeholder="Mínimo 6 caracteres"
                          className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                          value={form.newPassword}
                          onChange={(e) => setForm((prev) => ({ ...prev, newPassword: e.target.value }))}
                          disabled={isLoading}
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => setShowNew((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {form.newPassword.length > 0 && !meetsPasswordPolicy(form.newPassword) && (
                        <p className="text-sm text-red-500">{PASSWORD_POLICY_MESSAGE}</p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="confirmPassword" className="text-gray-700">Confirmar Nova Senha</Label>
                      <div className="relative">
                        <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                        <Input
                          id="confirmPassword"
                          type={showConfirm ? 'text' : 'password'}
                          placeholder="Repita a nova senha"
                          className="pl-11 pr-11 h-12 bg-white border-gray-200 focus-visible:border-[#F5B322] focus-visible:ring-[#F5B322]"
                          value={form.confirmPassword}
                          onChange={(e) => setForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
                          disabled={isLoading}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm((prev) => !prev)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                        </button>
                      </div>
                      {form.confirmPassword.length > 0 && form.newPassword !== form.confirmPassword && (
                        <p className="text-sm text-red-500">As senhas não coincidem.</p>
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-semibold bg-[#F5B322] hover:bg-[#D89A15] text-[#2F2117]"
                      disabled={isLoading || !isValid}
                    >
                      {isLoading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-4 w-4 border-2 border-[#2F2117]/30 border-t-[#2F2117] rounded-full animate-spin" />
                          Redefinindo...
                        </div>
                      ) : (
                        'Salvar nova senha'
                      )}
                    </Button>
                  </form>

                  <div className="text-center">
                    <Link
                      to="/login"
                      className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      Voltar ao login
                    </Link>
                  </div>
                </>
            );
          })()}
        </div>
      </div>

      <div
        className="hidden lg:flex lg:w-1/2 p-12 flex-col justify-center items-center relative overflow-hidden"
        style={{ background: BRAND_HERO_GRADIENT }}
      >
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>

        <div className="relative z-10 text-center space-y-6 max-w-md">
          <img src="/logo-librattum.png" alt="Librattum" className="h-10 mx-auto" />
          <div className="space-y-4">
            <h1 className="text-3xl font-bold text-white leading-tight">
              Recupere o acesso com segurança
            </h1>
            <p className="text-gray-300 text-lg">
              O link enviado por email é temporário e foi criado exclusivamente para redefinir sua senha.
            </p>
          </div>
          <div className="space-y-3 text-left">
            {[
              'Use pelo menos 6 caracteres',
              'Inclua ao menos uma letra maiúscula e um número',
              'Evite reutilizar senhas antigas ou pessoais',
            ].map((tip) => (
              <div key={tip} className="flex items-center gap-3 p-3 rounded-lg bg-white/10 border border-white/20">
                <ShieldCheck className="h-5 w-5 text-[#F5B322] flex-shrink-0" />
                <span className="text-sm text-gray-200">{tip}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
